import type { OnChangeFn, RowSelectionState, SortingState } from "@tanstack/react-table"
import React, { useCallback } from "react"

import { columns } from "./instructionSimpleColumns"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Instruction } from "@/graphql/codegen/graphql"

interface AssociatedInstructionsTableProps {
  instructions: Instruction[]
  instructionsFetching?: boolean
  associatedInstructionIds?: string[]
  selected: Record<string, boolean>
  onInstructionSelection: (selected: Record<string, boolean>) => void
  onInstructionSorted: (sorting: SortingState) => void
  sorting: SortingState
}

export const InstructionsToAssociateTable: React.FC<AssociatedInstructionsTableProps> = ({
  instructions,
  instructionsFetching = false,
  onInstructionSelection,
  onInstructionSorted,
  associatedInstructionIds = [],
  selected,
  sorting,
}) => {
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater
    onInstructionSorted(newSorting)
  }

  const handleRowSelectionChange = useCallback(
    (updaterOrValue: ((old: RowSelectionState) => RowSelectionState) | RowSelectionState) => {
      const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(selected) : updaterOrValue
      onInstructionSelection(newSelection)
    },
    [selected, onInstructionSelection]
  )
  return (
    <AerosBaseTable
      slotProps={{
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      features={{
        tanstackOptions: {
          data: instructions,
          columns,
          onRowSelectionChange: handleRowSelectionChange,
          getRowId: (row) => row?.instructionId,
          state: {
            isLoading: instructionsFetching,
            rowSelection: selected,
            sorting,
          },
          onSortingChange: handleSortingChange,
          // prevent selection of rows that are already associated
          enableRowSelection: (row) => !associatedInstructionIds.includes(row.original.instructionId),
        },
      }}
      noDataMessage='No instructions added'
    />
  )
}
